import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useColorScheme} from "@mui/material/styles";
import {ChatboxColors} from "crisp-sdk-web";
import CrispService from "../../../../../core/services/crisp";
import {CloseRounded, SupportAgentRounded} from "@mui/icons-material";
import {EndActionType} from "../index";
import './index.scss';
import {useTranslation} from "react-i18next";
import {usePartnerInformation} from "../../../../hooks/use-partner-information";
import {useOperatorInformation} from "../../../../hooks/use-operator-information";
import CacheService from "../../../../../core/services/cache/cache_service";

const useSupportChatEndAction = (): EndActionType => {
    const [chatOpen, setChatOpen] = useState(false);
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.app-bar.actions.support-chat'});
    const partnerInformation = usePartnerInformation();
    const operatorInformation = useOperatorInformation();
    const {mode, systemMode} = useColorScheme();
    const effectiveColorMode = mode === 'system'
        ? (systemMode ?? CrispService.resolveDocumentColorMode())
        : (mode ?? CrispService.resolveDocumentColorMode());

    /**
     * Keeps Crisp chatbox appearance in sync with the active MUI color scheme.
     */
    useEffect(() => {
        CrispService.syncAppearance({
            mode: effectiveColorMode === 'dark' ? 'dark' : 'light',
            theme: ChatboxColors.DeepPurple,
        });
    }, [effectiveColorMode]);

    /**
     * As soon as the panel is mounted:
     * - shows the chat box.
     * - hides the chat box on unmount.
     */
    useEffect(() => {
        CrispService.showChatBox();
        return () => CrispService.hideChatBox();
    }, []);

    /**
     * As soon as the operator or partner information is updated:
     * - sets the user information in the Crisp chat box.
     */
    useEffect(() => {
        CrispService.setUser({
            email: operatorInformation?.email ?? "",
            nickname: operatorInformation?.name ?? "",
            avatar: CacheService.getOperatorProfileImage(operatorInformation?.id),
            company: partnerInformation?.name ?? "",
        })
    }, [operatorInformation?.email, operatorInformation?.id, operatorInformation?.name, partnerInformation?.name]);

    /**
     * As soon as the component is mounted:
     * - registers the event listeners for the chat box.
     */
    useEffect(() => {
        CrispService.onChatBoxOpened(() => setChatOpen(true));
        CrispService.onChatBoxClosed(() => setChatOpen(false));
        CrispService.closeChatBox();
        return () => CrispService.closeChatBox();
    }, []);

    /**
     * As soon as the chat box is opened:
     * - registers the event listener to close the chat box when clicked outside.
     */
    useEffect(() => {
        if (!chatOpen) {
            return;
        }
        const listener: (ev: WindowEventMap['click']) => void = (ev) => {
            if (ev.target instanceof HTMLElement && (ev.target.matches('#crisp-chatbox') || ev.target.closest('#crisp-chatbox'))) {
                return;
            }
            CrispService.closeChatBox();
            window.removeEventListener('click', listener);
        };
        setTimeout(() => window.addEventListener('click', listener), 100);
        return () => window.removeEventListener('click', listener);
    }, [chatOpen]);

    /**
     * Opens the support chat of the application.
     */
    const toggleSupportChat = useCallback<React.MouseEventHandler<HTMLElement>>(() => {
        if (chatOpen || CrispService.isChatOpened) {
            CrispService.closeChatBox();
        } else {
            CrispService.openChatBox();
        }
    }, [chatOpen]);

    return useMemo<EndActionType>(() => ({
        key: "supportChat",
        title: chatOpen ? t(t => t.close) : t(t => t.open),
        icon: chatOpen
            ? <CloseRounded/>
            : <SupportAgentRounded/>,
        color: "primary",
        onClick: toggleSupportChat,
    }), [chatOpen, toggleSupportChat, t])
}

export default useSupportChatEndAction;
